// Guest flow, step 1 of 3: capture contactEmail and request an OTP.
// POST /guest/otp/request ALWAYS responds { sent: true } regardless of
// outcome (backend/src/routes/guestRoutes.js — never leaks whether an email
// is "known") — this step always proceeds to verify on submit; there is no
// "email not found" state to build here, by the endpoint's own contract.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import ErrorState from './ErrorState.jsx';
import styles from './GuestEmailStep.module.css';

// idPrefix (added for the customer-registration task): swaps every string
// id from 'portal.guestEmail.*' to 'portal.<idPrefix>.*' so
// pages/RegisterPage.jsx can reuse this exact component/behavior with
// registration-specific copy instead of a near-duplicate component —
// portal/CLAUDE.md's 120KB budget makes that reuse worth the extra prop.
// Defaults to 'guestEmail' so every existing caller (pages/GuestPage.jsx) is
// unaffected.
export default function GuestEmailStep({
  defaultEmail,
  onSubmit,
  isSubmitting,
  noticeId,
  submitErrorId,
  submitError,
  idPrefix = 'guestEmail',
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState(defaultEmail || '');
  const [touched, setTouched] = useState(false);

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    onSubmit(email.trim());
  };

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h1 className={styles.title}>{t(`portal.${idPrefix}.title`)}</h1>
      <p className={styles.subtitle}>{t(`portal.${idPrefix}.subtitle`)}</p>

      {noticeId && <ErrorState messageId={noticeId} />}
      {submitErrorId && <ErrorState messageId={submitErrorId} error={submitError} />}

      <label className={styles.field}>
        <span className={styles.label}>{t(`portal.${idPrefix}.label`)}</span>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {touched && !isValid && (
          <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
        )}
      </label>

      <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
        {isSubmitting ? t(`portal.${idPrefix}.submitting`) : t(`portal.${idPrefix}.submit`)}
      </button>
    </form>
  );
}
